import { App } from '@capacitor/app';
import { PushNotifications, type Token } from '@capacitor/push-notifications';
import { isNative, platform } from '../platform';
import { supabase } from '../supabase';
import posthog from '../posthog';

type PushHandlers = {
  onReceived?: (title: string, body: string, data: Record<string, unknown>) => void;
  onActionTap?: (data: Record<string, unknown>) => void;
};

/**
 * Report each step of push registration to analytics (never the token itself),
 * so a phone that can't register shows exactly where it stops.
 */
function report(stage: string, props: Record<string, unknown> = {}) {
  try {
    posthog.capture('push_registration', { stage, platform, ...props });
  } catch {
    // Analytics must never get in the way of registering.
  }
}

/**
 * Register the device for push notifications and persist the resulting token
 * against the signed-in user so the backend can target them. No-op on web.
 * Returns a cleanup function that removes the listeners.
 */
export async function registerPush(handlers: PushHandlers = {}): Promise<() => void> {
  if (!isNative) return () => {};

  const perm = await PushNotifications.checkPermissions();
  let receive = perm.receive;
  if (receive === 'prompt' || receive === 'prompt-with-rationale') {
    receive = (await PushNotifications.requestPermissions()).receive;
  }
  report('permission', { result: receive });
  if (receive !== 'granted') return () => {};

  const handles = [
    await PushNotifications.addListener('registration', (token: Token) => {
      report('token_received');
      void savePushToken(token.value);
    }),
    await PushNotifications.addListener('registrationError', (err) => {
      console.error('Push registration error', err);
      report('registration_error', { error: String((err as { error?: unknown }).error ?? err).slice(0, 300) });
    }),
    await PushNotifications.addListener('pushNotificationReceived', (notif) => {
      handlers.onReceived?.(notif.title ?? '', notif.body ?? '', notif.data ?? {});
    }),
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      handlers.onActionTap?.(action.notification.data ?? {});
    }),
  ];

  report('registering');
  await PushNotifications.register();

  return () => handles.forEach((h) => void h.remove());
}

/** Upsert the device token into Supabase, scoped to the current user. */
async function savePushToken(token: string): Promise<void> {
  const { data } = await supabase.auth.getUser();
  const userId = data.user?.id;
  if (!userId) {
    report('save_skipped_no_user');
    return;
  }

  // The bundle id tells the server which app (traveler or host) this token is for.
  let appId: string | null = null;
  try {
    appId = (await App.getInfo()).id;
  } catch {
    // Older shells without App.getInfo: the server falls back to the traveler app.
  }

  const { error } = await supabase
    .from('push_tokens')
    .upsert(
      { user_id: userId, token, platform, app_id: appId, updated_at: new Date().toISOString() },
      { onConflict: 'token' },
    );
  if (error) {
    console.error('Saving push token failed', error);
    report('save_error', { error: error.message.slice(0, 300), code: error.code });
  } else {
    report('saved', { app_id: appId });
  }
}
